import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Users, TrendingUp, Award, Target, Plus, Copy, Sparkles, Printer, Calendar, Filter, Trash2, Eye, Pencil } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { PromotionalCodeManager } from './PromotionalCodeManager';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from 'recharts';

const COLORS = [
  'hsl(var(--primary))',
  'hsl(210, 70%, 50%)',
  'hsl(150, 60%, 45%)',
  'hsl(280, 60%, 55%)',
  'hsl(30, 80%, 55%)',
  'hsl(0, 70%, 55%)',
];

type RewardType = 'credits' | 'subscription_standard' | 'subscription_premium' | 'subscription_ultimate' | 'subscription_days' | 'credit_package' | 'custom';

const CREDIT_PACKAGES: Record<string, number> = { starter: 10, basic: 25, premium: 50, ultimate: 100 };

export const AdminReferralsPanel = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [customCode, setCustomCode] = useState('');
  const [codeQuantity, setCodeQuantity] = useState('5');
  const [validityType, setValidityType] = useState<'indefinite' | 'range'>('indefinite');
  const [validFrom, setValidFrom] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [rewardType, setRewardType] = useState<RewardType>('credits');
  const [rewardValue, setRewardValue] = useState(5);
  const [creditPackageType, setCreditPackageType] = useState('starter');
  const [campaignName, setCampaignName] = useState('Marketing Referral');
  const [selectedCodes, setSelectedCodes] = useState<Set<string>>(new Set());
  const [campaignFilter, setCampaignFilter] = useState('all');

  const { data: analytics, isLoading } = useQuery({
    queryKey: ['admin-referral-analytics'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('admin_get_referral_analytics');
      if (error) throw error;
      return data as any;
    }
  });

  // Load marketing codes from DB for persistence
  const { data: marketingCodes, isLoading: codesLoading } = useQuery({
    queryKey: ['marketing-referral-codes'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('promotional_codes')
        .select('*')
        .ilike('campaign_name', 'Marketing%')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    }
  });

  const getRewardPayload = () => {
    let reward_type_db = rewardType as string;
    let reward_value_db = rewardValue;
    let reward_description: string | null = null;

    if (rewardType === 'credit_package') {
      reward_type_db = 'credit_package';
      reward_value_db = CREDIT_PACKAGES[creditPackageType] || 10;
      reward_description = t(`promoCodes.creditPackages.${creditPackageType}`);
    }

    return { reward_type: reward_type_db, reward_value: reward_value_db, reward_description };
  };

  const createCodeMutation = useMutation({
    mutationFn: async (code: string) => {
      const finalCode = code.toUpperCase().replace(/[^A-Z0-9-]/g, '');
      if (finalCode.length < 4) throw new Error(t('adminReferrals.codeMinLength'));
      
      const { data: existing } = await supabase
        .from('promotional_codes')
        .select('code')
        .eq('code', finalCode)
        .single();
      
      if (existing) throw new Error(t('adminReferrals.codeExists'));
      
      const { data: existingUser } = await supabase
        .from('users')
        .select('referral_code')
        .eq('referral_code', finalCode)
        .single();
      
      if (existingUser) throw new Error(t('adminReferrals.codeExists'));

      const rewardPayload = getRewardPayload();

      const { error } = await supabase
        .from('promotional_codes')
        .insert({
          code: finalCode,
          ...rewardPayload,
          description: `Marketing referral code - ${rewardPayload.reward_type}`,
          campaign_name: campaignName || 'Marketing Referral',
          is_active: true,
          max_uses_per_user: 1,
          ...(validityType === 'range' && validFrom ? { created_at: validFrom } : {}),
          ...(validityType === 'range' && validUntil ? { expires_at: validUntil } : {}),
        });
      if (error) throw error;
      
      return finalCode;
    },
    onSuccess: (code) => {
      setCustomCode('');
      queryClient.invalidateQueries({ queryKey: ['promotional-codes'] });
      queryClient.invalidateQueries({ queryKey: ['marketing-referral-codes'] });
      toast.success(t('adminReferrals.codeCreatedSuccess', { code }));
    },
    onError: (error: Error) => {
      toast.error(error.message);
    }
  });

  const deleteCodeMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('promotional_codes').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing-referral-codes'] });
      queryClient.invalidateQueries({ queryKey: ['promotional-codes'] });
      toast.success(t('promoCodes.deleted'));
    }
  });

  const deleteBulkMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const { error } = await supabase.from('promotional_codes').delete().in('id', ids);
      if (error) throw error;
    },
    onSuccess: () => {
      setSelectedCodes(new Set());
      queryClient.invalidateQueries({ queryKey: ['marketing-referral-codes'] });
      queryClient.invalidateQueries({ queryKey: ['promotional-codes'] });
      toast.success(t('promoCodes.deleted'));
    }
  });

  const generateRandomCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = 'PETIUS-';
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  const handleCreateCode = () => {
    const code = customCode.trim() || generateRandomCode();
    createCodeMutation.mutate(code);
  };

  const handleGenerateBulk = async (count: number) => {
    const codes: string[] = [];
    for (let i = 0; i < count; i++) {
      codes.push(generateRandomCode());
    }
    
    const rewardPayload = getRewardPayload();
    const insertData = codes.map(code => ({
      code,
      ...rewardPayload,
      description: `Marketing referral code (bulk) - ${rewardPayload.reward_type}`,
      campaign_name: campaignName || 'Marketing Referral',
      is_active: true,
      max_uses_per_user: 1,
      ...(validityType === 'range' && validUntil ? { expires_at: validUntil } : {}),
    }));
    
    const { error } = await supabase
      .from('promotional_codes')
      .insert(insertData);
    
    if (error) {
      toast.error(error.message);
      return;
    }
    
    queryClient.invalidateQueries({ queryKey: ['promotional-codes'] });
    queryClient.invalidateQueries({ queryKey: ['marketing-referral-codes'] });
    toast.success(t('adminReferrals.bulkGenerated', { count }));
  };

  const handleGenerateCustomQuantity = () => {
    const count = parseInt(codeQuantity) || 5;
    if (count < 1 || count > 100) {
      toast.error(t('adminReferrals.quantityRange'));
      return;
    }
    handleGenerateBulk(count);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success(t('adminReferrals.copiedToClipboard'));
  };

  const copyAllCodes = () => {
    const allCodes = (marketingCodes || []).map((c: any) => c.code).join('\n');
    navigator.clipboard.writeText(allCodes);
    toast.success(t('adminReferrals.allCodesCopied'));
  };

  const handlePrint = () => {
    window.print();
  };

  const toggleSelectCode = (id: string) => {
    setSelectedCodes(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulkDelete = () => {
    if (selectedCodes.size === 0) return;
    deleteBulkMutation.mutate(Array.from(selectedCodes));
  };

  // Group codes by campaign
  const campaigns = Array.from(new Set((marketingCodes || []).map((c: any) => c.campaign_name).filter(Boolean)));
  const filteredMarketingCodes = (marketingCodes || []).filter((c: any) => {
    if (campaignFilter === 'all') return true;
    return c.campaign_name === campaignFilter;
  });

  const getRewardDisplayLabel = (code: any) => {
    switch (code.reward_type) {
      case 'credits': return `${code.reward_value} Credits`;
      case 'subscription_standard': return `Standard - ${code.reward_value}d`;
      case 'subscription_premium': return `Premium - ${code.reward_value}d`;
      case 'subscription_ultimate': return `Ultimate - ${code.reward_value}d`;
      case 'subscription_days': return `${code.reward_value} days`;
      case 'credit_package': return code.reward_description || `${code.reward_value} Credits`;
      default: return code.reward_description || `${code.reward_value}`;
    }
  };
  
  if (isLoading) {
    return <Skeleton className="h-96" />;
  }
  
  const globalStats = analytics?.global_stats as any;
  const topReferrers = (analytics?.top_referrers || []) as any[];
  
  return (
    <div className="space-y-6 pb-24 print:p-4">
      {/* Print Button */}
      <div className="flex justify-end print:hidden">
        <Button variant="outline" size="sm" onClick={handlePrint}>
          <Printer className="h-4 w-4 mr-2" />
          {t('adminReferrals.print')}
        </Button>
      </div>

      {/* Promotional Codes Manager */}
      <div className="print:hidden">
        <PromotionalCodeManager />
      </div>

      {/* Code Generator Card */}
      <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-transparent print:hidden">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center justify-between flex-wrap gap-2">
            <span className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              {t('adminReferrals.marketingCodeGenerator')}
            </span>
            <div className="flex items-center gap-2">
              <Select value={campaignFilter} onValueChange={setCampaignFilter}>
                <SelectTrigger className="w-[160px] h-8">
                  <Filter className="h-3 w-3 mr-1" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('promoCodes.allCampaigns')}</SelectItem>
                  {campaigns.map(c => <SelectItem key={c} value={c || ''}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={handlePrint}>
                <Printer className="h-4 w-4 mr-1" />
                {t('adminReferrals.print')}
              </Button>
              <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                <DialogTrigger asChild>
                  <Button size="sm">
                    <Plus className="h-4 w-4 mr-1" />
                    {t('adminReferrals.createCode')}
                  </Button>
                </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>{t('adminReferrals.createReferralCode')}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>{t('adminReferrals.customCodeOptional')}</Label>
                    <Input
                      value={customCode}
                      onChange={(e) => setCustomCode(e.target.value.toUpperCase())}
                      placeholder={t('adminReferrals.codePlaceholder')}
                      className="mt-1 font-mono"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('adminReferrals.leaveEmptyForRandom')}
                    </p>
                  </div>

                  {/* Campaign Name */}
                  <div>
                    <Label>{t('promoCodes.campaignLabel')}</Label>
                    <Input
                      value={campaignName}
                      onChange={(e) => setCampaignName(e.target.value)}
                      placeholder="Marketing Referral"
                      className="mt-1"
                    />
                  </div>

                  {/* Reward Type Configuration */}
                  <div className="border-t pt-4">
                    <Label className="font-medium mb-3 block">{t('promoCodes.rewardType')}</Label>
                    <Select value={rewardType} onValueChange={(v) => setRewardType(v as RewardType)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="credits">{t('promoCodes.rewardTypes.credits')}</SelectItem>
                        <SelectItem value="subscription_standard">{t('promoCodes.rewardTypes.subscriptionStandard')}</SelectItem>
                        <SelectItem value="subscription_premium">{t('promoCodes.rewardTypes.subscriptionPremium')}</SelectItem>
                        <SelectItem value="subscription_ultimate">{t('promoCodes.rewardTypes.subscriptionUltimate')}</SelectItem>
                        <SelectItem value="subscription_days">{t('promoCodes.rewardTypes.subscriptionDays')}</SelectItem>
                        <SelectItem value="credit_package">{t('promoCodes.rewardTypes.creditPackage')}</SelectItem>
                        <SelectItem value="custom">{t('promoCodes.rewardTypes.custom')}</SelectItem>
                      </SelectContent>
                    </Select>

                    {/* Conditional value fields */}
                    {rewardType === 'credits' && (
                      <div className="mt-3">
                        <Label>{t('promoCodes.creditsAmount')}</Label>
                        <Input type="number" value={rewardValue} onChange={(e) => setRewardValue(parseInt(e.target.value) || 0)} min="1" />
                      </div>
                    )}
                    {(rewardType === 'subscription_standard' || rewardType === 'subscription_premium' || rewardType === 'subscription_ultimate' || rewardType === 'subscription_days') && (
                      <div className="mt-3">
                        <Label>{t('promoCodes.subscriptionDuration')}</Label>
                        <Input type="number" value={rewardValue} onChange={(e) => setRewardValue(parseInt(e.target.value) || 0)} min="1" placeholder="30" />
                      </div>
                    )}
                    {rewardType === 'credit_package' && (
                      <div className="mt-3">
                        <Label>{t('promoCodes.rewardTypes.creditPackage')}</Label>
                        <Select value={creditPackageType} onValueChange={(v) => { setCreditPackageType(v); setRewardValue(CREDIT_PACKAGES[v] || 10); }}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="starter">{t('promoCodes.creditPackages.starter')}</SelectItem>
                            <SelectItem value="basic">{t('promoCodes.creditPackages.basic')}</SelectItem>
                            <SelectItem value="premium">{t('promoCodes.creditPackages.premium')}</SelectItem>
                            <SelectItem value="ultimate">{t('promoCodes.creditPackages.ultimate')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  {/* Validity Configuration */}
                  <div className="border-t pt-4">
                    <Label className="flex items-center gap-2 mb-3">
                      <Calendar className="h-4 w-4" />
                      {t('adminReferrals.validityPeriod')}
                    </Label>
                    <Select value={validityType} onValueChange={(v) => setValidityType(v as 'indefinite' | 'range')}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="indefinite">{t('adminReferrals.indefinite')}</SelectItem>
                        <SelectItem value="range">{t('adminReferrals.specificRange')}</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    {validityType === 'range' && (
                      <div className="grid grid-cols-2 gap-2 mt-3">
                        <div>
                          <Label className="text-xs">{t('adminReferrals.validFrom')}</Label>
                          <Input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
                        </div>
                        <div>
                          <Label className="text-xs">{t('adminReferrals.validUntil')}</Label>
                          <Input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} min={validFrom} />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <Button onClick={handleCreateCode} className="flex-1" disabled={createCodeMutation.isPending}>
                      {createCodeMutation.isPending ? t('common.processing') : t('adminReferrals.createCode')}
                    </Button>
                  </div>
                  
                  <div className="border-t pt-4">
                    <p className="text-sm font-medium mb-2">{t('adminReferrals.bulkGenerate')}</p>
                    <div className="flex gap-2 flex-wrap">
                      <Button variant="outline" size="sm" onClick={() => handleGenerateBulk(5)}>
                        {t('adminReferrals.codes', { count: 5 })}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleGenerateBulk(10)}>
                        {t('adminReferrals.codes', { count: 10 })}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleGenerateBulk(25)}>
                        {t('adminReferrals.codes', { count: 25 })}
                      </Button>
                    </div>
                    
                    <div className="flex gap-2 mt-3">
                      <Input
                        type="number"
                        value={codeQuantity}
                        onChange={(e) => setCodeQuantity(e.target.value)}
                        min="1"
                        max="100"
                        className="w-24"
                        placeholder="50"
                      />
                      <Button variant="outline" size="sm" onClick={handleGenerateCustomQuantity}>
                        {t('adminReferrals.generateCustom')}
                      </Button>
                    </div>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredMarketingCodes.length > 0 ? (
            <>
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <p className="text-sm text-muted-foreground">
                  {t('adminReferrals.codesGenerated', { count: filteredMarketingCodes.length })}
                </p>
                <div className="flex gap-1">
                  {selectedCodes.size > 0 && (
                    <Button variant="destructive" size="sm" onClick={handleBulkDelete} disabled={deleteBulkMutation.isPending}>
                      <Trash2 className="h-3 w-3 mr-1" />
                      {t('common.delete')} ({selectedCodes.size})
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={copyAllCodes}>
                    <Copy className="h-3 w-3 mr-1" />
                    {t('adminReferrals.copyAll')}
                  </Button>
                </div>
              </div>
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {filteredMarketingCodes.map((code: any) => (
                  <div key={code.id} className="flex items-center justify-between gap-2 p-2 rounded-lg hover:bg-muted/50 border border-transparent hover:border-border">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={selectedCodes.has(code.id)}
                        onCheckedChange={() => toggleSelectCode(code.id)}
                      />
                      <Badge variant="secondary" className="font-mono cursor-pointer" onClick={() => copyToClipboard(code.code)}>
                        {code.code}
                        <Copy className="h-3 w-3 ml-1 opacity-50" />
                      </Badge>
                      <span className="text-xs text-muted-foreground">{getRewardDisplayLabel(code)}</span>
                      {code.campaign_name && <Badge variant="outline" className="text-xs">{code.campaign_name}</Badge>}
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteCodeMutation.mutate(code.id)}>
                        <Trash2 className="h-3 w-3 text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-6 text-muted-foreground text-sm">
              {t('promoCodes.noCodes')}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Global Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="print:border print:shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="h-4 w-4 text-blue-500" />
              {t('adminReferrals.totalUsersWithCode')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{globalStats?.total_users_with_code || 0}</p>
          </CardContent>
        </Card>
        
        <Card className="print:border print:shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-green-500" />
              {t('adminReferrals.totalReferredUsers')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{globalStats?.total_referred_users || 0}</p>
          </CardContent>
        </Card>
        
        <Card className="print:border print:shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Award className="h-4 w-4 text-yellow-500" />
              {t('adminReferrals.completedReferrals')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{globalStats?.total_completed_referrals || 0}</p>
          </CardContent>
        </Card>
        
        <Card className="print:border print:shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Target className="h-4 w-4 text-purple-500" />
              {t('adminReferrals.conversionRate')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{globalStats?.conversion_rate || 0}%</p>
          </CardContent>
        </Card>
      </div>

      {/* Statistical Charts */}
      {(globalStats?.total_users_with_code > 0 || topReferrers.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 print:grid-cols-2">
          {/* Conversion Funnel Pie */}
          <Card className="print:border print:shadow-none print:break-inside-avoid">
            <CardHeader>
              <CardTitle className="text-base">{t('adminReferrals.conversionFunnel')}</CardTitle>
              <CardDescription>{t('adminReferrals.conversionFunnelDesc')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={[
                        { name: t('adminReferrals.completedReferrals'), value: globalStats?.total_completed_referrals || 0, fill: COLORS[2] },
                        { name: t('adminReferrals.pendingReferrals'), value: Math.max(0, (globalStats?.total_referred_users || 0) - (globalStats?.total_completed_referrals || 0)), fill: COLORS[4] },
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Top Referrers Bar Chart */}
          {topReferrers.length > 0 && (
            <Card className="print:border print:shadow-none print:break-inside-avoid">
              <CardHeader>
                <CardTitle className="text-base">{t('adminReferrals.topReferrersChart')}</CardTitle>
                <CardDescription>{t('adminReferrals.topReferrersChartDesc')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={topReferrers.slice(0, 5).map(r => ({
                        name: r.full_name?.split(' ')[0] || t('adminReferrals.anonymous'),
                        total: r.total_referrals,
                        completed: r.completed_referrals,
                      }))}
                      layout="vertical"
                      margin={{ left: 60 }}
                    >
                      <XAxis type="number" />
                      <YAxis type="category" dataKey="name" width={55} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="total" name={t('adminReferrals.total')} fill={COLORS[0]} />
                      <Bar dataKey="completed" name={t('adminReferrals.completed')} fill={COLORS[2]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
      
      {/* Top Referrers Table */}
      <Card className="print:border print:shadow-none print:break-inside-avoid">
        <CardHeader>
          <CardTitle>{t('adminReferrals.topReferrers')}</CardTitle>
        </CardHeader>
        <CardContent>
          {topReferrers.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('adminReferrals.user')}</TableHead>
                  <TableHead>{t('adminReferrals.email')}</TableHead>
                  <TableHead>{t('adminReferrals.referralCode')}</TableHead>
                  <TableHead className="text-center">{t('adminReferrals.total')}</TableHead>
                  <TableHead className="text-center">{t('adminReferrals.completed')}</TableHead>
                  <TableHead className="text-center">{t('adminReferrals.creditsEarned')}</TableHead>
                  <TableHead>{t('adminReferrals.lastReferral')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {topReferrers.map((referrer: any) => (
                  <TableRow key={referrer.user_id}>
                    <TableCell className="font-medium">{referrer.full_name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{referrer.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-mono">{referrer.referral_code}</Badge>
                    </TableCell>
                    <TableCell className="text-center font-bold">{referrer.total_referrals}</TableCell>
                    <TableCell className="text-center text-green-600">{referrer.completed_referrals}</TableCell>
                    <TableCell className="text-center text-yellow-600">{referrer.total_credits_earned}</TableCell>
                    <TableCell className="text-sm">
                      {referrer.last_referral_date ? new Date(referrer.last_referral_date).toLocaleDateString() : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-center text-muted-foreground py-8">{t('adminReferrals.noReferrersYet')}</p>
          )}
        </CardContent>
      </Card>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print\\:p-4, .print\\:p-4 * {
            visibility: visible;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:border {
            border: 1px solid #e5e7eb !important;
          }
          .print\\:shadow-none {
            box-shadow: none !important;
          }
        }
      `}</style>
    </div>
  );
};
